/**
 * The JSON wire format of a google.protobuf.Timestamp.
 *
 * https://developers.google.com/protocol-buffers/docs/proto3#json
 * Timestamp string e.g. "1972-01-01T10:00:20.021Z"
 *
 * Uses RFC 3339, where generated output will always be Z-normalized
 * and uses 0, 3, 6 or 9 fractional digits. Offsets other than "Z" are
 * also accepted.
 */
export type timestamp = string & { timestampBrand: 'brand' } | { timestampBrand: 'brand' };

// A precise representation of google.protobuf.Timestamp.
export interface Timestamp {
  // Represents seconds of UTC time since Unix epoch
  // 1970-01-01T00:00:00Z. Must be from 0001-01-01T00:00:00Z to
  // 9999-12-31T23:59:59Z inclusive.
  seconds: number

  // Non-negative fractions of a second at nanosecond resolution. Negative
  // second values with fractions must still have non-negative nanos values
  // that count forward in time. Must be from 0 to 999,999,999
  // inclusive.
  nanos: number
}

export namespace Timestamp {
  export const nil = '' as timestamp
  export const epoch = '1970-01-01T00:00:00Z' as timestamp
  export const zero = '0001-01-01T00:00:00Z' as timestamp

  // TODO: tighten this up to specify max digits?
  const reFmt = /^-?\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(.\d+)?Z$/

  // Returns now as a timestamp.
  export function now(): timestamp {
    return dateToString(new Date()) as timestamp
  }

  // Convert a Date into a timestamp.
  export function fromDate(date: Date | null): timestamp | null {
    if (!date) {
      return null
    }
    return dateToString(date) as timestamp
  }

  // Parse a timestamp into a Date
  export function toDate(ts: timestamp | null | undefined): Date | null {
    if (!ts) {
      return null
    }

    let t = ts as string
    if (!t.match(reFmt)) {
      return new Date('')
    }

    return new Date(t)
  }

  // Parse a timestamp into a Date; returns an Invalid Date if ts is nil or invalid.
  export function mustDate(ts: timestamp | null | undefined): Date {
    const date = toDate(ts)

    if (!date) {
      return new Date('')
    }
    return date
  }

  // Convert milliseconds since January 1, 1970, 00:00:00 UTC into a timestamp.  Any fractional millis are truncated.
  export function fromMillis(millis: number): timestamp {
    return numToString(Math.trunc(millis)) as timestamp
  }

  // Parse a timestamp into milliseconds since January 1, 1970, 00:00:00 UTC.
  export function toMillis(ts: timestamp | null | undefined): number {
    if (!ts) {
      return 0
    }

    let t = ts as string
    if (!t.match(reFmt)) {
      return NaN
    }
    return Date.parse(t)
  }

  // Convert a Timestamp to a timestamp.
  export function fromProto(ts: Timestamp | null): timestamp | null {
    if (!ts) {
      return null
    }

    let str = numToString(ts.seconds * 1000)
    if (ts.nanos == 0) {
      return str as timestamp
    }
    str = str.slice(0, str.length - 1)

    let tail = ts.nanos.toString()
    tail = tail.padStart(9, '0')
    while (tail.endsWith('000')) {
      tail = tail.substring(0, tail.length - 3)
    }
    return (str + '.' + tail + 'Z') as timestamp
  }

  // Parse a timestamp into a Timestamp, which exactly represents the value of the input
  // Returns null if the input cannot be parsed.
  export function toProto(ts: timestamp | null): Timestamp | null {
    if (!ts) {
      return null
    }

    let t = ts as string
    if (!t.match(reFmt)) {
      return null
    }

    let pos = t.indexOf(".")
    if (pos < 0) {
      // e.g. 0001-01-01T00:00:00Z
      let millis = Date.parse(t)
      return { seconds: millis / 1000, nanos: 0 }
    } else {
      // e.g. 0001-01-01T00:00:00.000000Z
      let millis = Date.parse(t.slice(0, pos) + 'Z')
      let nanos = parseInt(t.slice(pos + 1, t.length - 1).substring(0, 9).padEnd(9, '0'), 10);
      return {
        seconds: millis / 1000,
        nanos: nanos,
      }
    }
  }

  function numToString(millis: number): string {
    return dateToString(new Date(millis))
  }

  function dateToString(date: Date): string {
    let str = date.toISOString()
    if (str.endsWith(".000Z")) {
      str = str.slice(0, str.length - 5) + "Z"
    }
    return str
  }
}
